import { useContext, useEffect, useRef, useState } from 'react';
import { deleteMessage, getMeeting, getMeetings, highlightMessage } from '../../../../services/services';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { MeetingChatMessage } from '../../../../interfaces/interfaces';
import { usePermission } from '../../../../hooks/usePermission';
import { useReportUser } from '../../../meeting/hooks/useReportUser';
import { useTranslation } from 'next-i18next';
import { Popover } from '@mui/material';
import { MessageContext } from '.';
import { BlockUserButton } from '../../ChatModeration/BlockUserButton';

export interface MessageOptionsProps {
  message: MeetingChatMessage;
  enableHighlights: boolean;
}

export const MessageOptions = ({ message, enableHighlights }: MessageOptionsProps) => {
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const meeting = useSelector((state) => state.video.meeting);
  const isBannedFromChat = useSelector((state) => state.chat.isBannedFromChat);

  const { setReplyTo, fetchMessages } = useContext(MessageContext);
  const { t } = useTranslation();
  const { isEventHost, isBoothHost } = usePermission();
  const { showReportModal } = useReportUser();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const isOwnMessage = user?._id === message.user._id;

  const isBoothOrEventHost = isEventHost(user, event) || isBoothHost(user, meeting?.booth ? [meeting.booth] : []);

  async function toggleHighlightedMessage() {
    await highlightMessage(message._id);
    fetchMessages();
  }

  async function handleDelete() {
    try {
      await deleteMessage(message._id);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      //@ts-ignore
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', (e) => handleClickOutside(e));
    return () => {
      document.removeEventListener('mousedown', (e) => handleClickOutside(e));
    };
  }, [dropdownRef]);

  if (isBannedFromChat) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        ref={anchorRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className={`inline-flex self-center items-center p-2 text-sm font-medium text-center text-gray-900 bg-inherit rounded-lg`}>
        <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 4 15">
          <path d="M3.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 6.041a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 5.959a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
        </svg>
      </button>
      <Popover open={showDropdown} anchorEl={anchorRef.current}>
        <ul className="text-sm text-gray-700 dark:text-gray-200" aria-labelledby="dropdownMenuIconButton">
          <li onClick={() => setReplyTo(message)} className="px-4 py-2 cursor-pointer text-slate-700 hover:bg-slate-200">
            {t('chat:reply')}
          </li>
          {enableHighlights && isEventHost(user, event) && (
            <li onClick={() => toggleHighlightedMessage()} className="px-4 py-2 cursor-pointer text-slate-700 hover:bg-slate-200">
              {t('chat:highlight')}
            </li>
          )}
          {!isOwnMessage && (
            <li
              onClick={() => showReportModal(message.user?._id, message.user?.name, message._id)}
              className="px-4 py-2 cursor-pointer text-slate-700 hover:bg-slate-200">
              {t('chat:report')}
            </li>
          )}
          {/* consider allowing booth hosts to delete message */}
          {isEventHost(user, event) && (
            <li className="px-4 py-2 cursor-pointer text-slate-700 hover:bg-slate-200" onClick={handleDelete}>
              {t('event:stage.delete-single-message')}
            </li>
          )}
          {isBoothOrEventHost && !isOwnMessage && (
            <BlockUserButton
              userId={message.user._id}
              boothId={meeting?.booth?._id}
              messageId={message._id}
              element={<li className="px-4 py-2 w-full cursor-pointer text-slate-700 hover:bg-slate-200">{t('chat:block-guest-immediately')}</li>}
            />
          )}
        </ul>
      </Popover>
    </div>
  );
};
